import React, { useMemo, useState, useEffect } from "react";
import { axInst } from "../../Components/Login/Service";
import { useQuery } from "@tanstack/react-query";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useSelector } from "react-redux";
import WaterMark from "../../Components/WaterMark/WaterMark.jsx";
import Select from "react-select";
import { Loader } from "rsuite";
import { TbFilterDown } from "react-icons/tb";

// API call for modal data
const ModalDataApi = async (endpoint, body) => {
  const res = await axInst.post(endpoint, body);
  return res.data || [];
};

// API call for expanded row data
const fetchExpandedRowData = async (spmName) => {
  const body = { Spm_Name: [spmName] };
  const res = await axInst.post("dealer-sales-success-centre/nfa_next", body);
  return res.data || [];
};

// 👇 Expanded Row
const ExpandedDetailPanel = ({ rowIndex, spmName }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExpandedData = async () => {
      try {
        const res = await fetchExpandedRowData(spmName);
        setData(res);
      } catch (err) {
        console.error("Failed to fetch expanded data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchExpandedData();
  }, [spmName]);

  if (loading) return <div className="p-2 text-sm">Loading...</div>;

  return (
    <div className="text-xs">
      {data && data.length > 0 ? (
        <table className="w-full text-left border border-collapse text-xs">
          <thead>
            <tr>
              {Object.keys(data[0]).map((key) => (
                <th
                  key={key}
                  className="border px-2 py-1 bg-slate-600 text-white"
                >
                  {key.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                {Object.values(row).map((val, j) => (
                  <td key={j} className="border px-2 py-1">
                    {val}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div>No data</div>
      )}
    </div>
  );
};

const ModalMuiTable = ({
  open,
  heading,
  apiEndpoint,
  onClose,
  FilterComponent = null,
  showFilterButton = true,
  sliceName = null,
  nfaBody = null,
}) => {
  // const { data, submitted } = useSelector((e) => e.filtersOfftake);
  const { data, submitted } = useSelector((state) => state[sliceName] || {});

  const [selectedOption, setSelectedOption] = useState("2");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [isFormVisible, setFormVisible] = useState(false);

  // 🔍 Search state + debounce
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0); // reset to first page when searching
    }, 1000);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // 🔥 Fetch Data
  const { data: ModalData = [], isLoading } = useQuery({
    queryKey: [
      "Modal Data",
      apiEndpoint,
      submitted,
      open,
      selectedOption,
      page,
      pageSize,
      debouncedSearch,
    ],
    queryFn: () => {
      let finalEndpoint = apiEndpoint;
      if (heading === "DSE with C0" && selectedOption) {
        finalEndpoint = `${apiEndpoint}?day=${selectedOption}`;
      }
      const from = page * pageSize;
      finalEndpoint += finalEndpoint.includes("?")
        ? `&from=${from}&size=${pageSize}`
        : `?from=${from}&size=${pageSize}`;
      return ModalDataApi(finalEndpoint, {
        ...data,
        searching_text: debouncedSearch || "",
        ...nfaBody,
      });
    },
    enabled: open && !!apiEndpoint,
    staleTime: 0,
    cacheTime: 0,
    keepPreviousData: true,
  });

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Columns
  const columns = useMemo(() => {
    if (!ModalData || ModalData.length === 0) return [];
    return Object.keys(ModalData[0] || {}).map((key) => {
      if (key === "Growth") {
        return {
          accessorKey: key,
          header: "Growth",
          Cell: ({ cell }) => {
            const value = cell.getValue();
            const cleaned = String(value).replace(/₹|,/g, "").trim();
            const num = parseFloat(cleaned);
            const isPositive = !isNaN(num) && num >= 0;
            const formatted = new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
              maximumFractionDigits: 2,
            }).format(num);
            return (
              <span style={{ color: isPositive ? "green" : "red" }}>
                {formatted}
              </span>
            );
          },
        };
      }
      return {
        accessorKey: key,
        header: key.replace(/_/g, " "),
      };
    });
  }, [ModalData]);

  const table = useMaterialReactTable({
    columns,
    data: ModalData,
    enableColumnOrdering: true,
    enablePagination: false,
    enableGlobalFilter: false,
    enableStickyHeader: true,
    enableStickyFooter: false,
    enableTableFooter: false,
    enableBottomToolbar: false,
    initialState: { density: "compact" },
    enableColumnResizing: false,
    muiTopToolbarProps: { sx: { minHeight: "37px", paddingY: "0px" } },
    renderTopToolbarCustomActions: () => (
      <div className="flex justify-end w-full gap-2 ">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search..."
          className="border px-2 py-1 text-sm rounded w-64"
        />
        {showFilterButton && FilterComponent && (
          <button title="Click to Apply Filters" onClick={toggleForm}>
            <TbFilterDown className="w-6 h-6 text-[#757575]" />
          </button>
        )}
      </div>
    ),

    muiToolbarInternalToolbarProps: {
      sx: {
        "& .MuiIconButton-root": { padding: "4px!important", fontSize: "12px" },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "90vh",
        zIndex: 10,
        overflow: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "nowrap!important",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        color: "white",
        fontSize: "12px",
      },
    },
    muiTableBodyCellProps: { sx: { fontSize: "12px" } },

    ...(heading === "NFAs approved within recommendation" && {
      enableExpanding: true,
      getRowCanExpand: () => true,
      renderDetailPanel: ({ row }) => (
        <ExpandedDetailPanel
          rowIndex={row.index}
          spmName={row.original?.SPM_NAME}
        />
      ),
    }),
  });

  if (!open) return null;

  // Dropdown Options
  const options = [
    { value: "", label: "All" },
    ...Array.from({ length: 31 }, (_, i) => ({
      value: `${i + 1}`,
      label: `${i + 1}`,
    })),
  ];

  const handlePrevPage = () => {
    if (page > 0) setPage((prev) => prev - 1);
  };
  const handleNextPage = () => {
    if (ModalData && ModalData.length === pageSize) {
      setPage((prev) => prev + 1);
    }
  };
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  const toggleForm = () => setFormVisible(!isFormVisible);

  // ---------------- UI ----------------
  return (
    <div
      key={ModalData.length}
      className="fixed mui-modal inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div className="bg-white rounded-lg shadow-lg p-2 w-[95%] max-w-6xl max-h-[96vh] overflow-auto relative">
        <div className="text-lg font-semibold mb-[2px]">{heading}</div>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-lg font-bold w-6 h-6 rounded-full bg-[#2a5775] text-white flex items-center justify-center hover:bg-blue-600"
        >
          &times;
        </button>

        {/* Top Controls: Dropdown + Search */}
        <div className="flex justify-between items-center mb-2">
          {heading === "DSE with C0" && (
            <div className="mb-1 w-28">
              <label className="block text-sm font-medium text-gray-700">
                Select Day:
              </label>
              <Select
                value={options.find((opt) => opt.value === selectedOption)}
                onChange={(selected) => {
                  setSelectedOption(selected.value);
                  setPage(0);
                }}
                options={options}
                placeholder="Select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (p) => ({
                    ...p,
                    minHeight: "20px",
                    height: "20px",
                    fontSize: "12px",
                  }),
                  indicatorsContainer: (p) => ({ ...p, height: "20px" }),
                  valueContainer: (p) => ({
                    ...p,
                    height: "20px",
                    padding: "0 4px",
                  }),
                  input: (p) => ({ ...p, margin: "0px", padding: "0px" }),
                  option: (p) => ({
                    ...p,
                    fontSize: "12px",
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    height: "20px",
                  }),
                }}
              />
            </div>
          )}
        </div>

        {showFilterButton && FilterComponent && (
          <div className="mb-1">
            {isFormVisible && (
              <FilterComponent
                isFormVisible={isFormVisible}
                setFormVisible={setFormVisible}
              />
            )}
          </div>
        )}

        <WaterMark />

        {/* Table */}
        <div className="border border-gray-300 rounded-md text-sm">
          {isLoading ? (
            <div className="p-4 text-center text-gray-600">
              <Loader />
            </div>
          ) : (
            <MaterialReactTable
              table={table}
              isLoading={isLoading}
              renderEmptyRowsFallback={() => (
                <div className="p-4 text-center text-gray-600 ">
                  No data available
                </div>
              )}
            />
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-end items-center mt-1 gap-3">
          <button
            onClick={handlePrevPage}
            disabled={page === 0}
            className="px-3 py-1 border text-xs rounded bg-[#2A5775] text-white disabled:opacity-50"
          >
            Prev
          </button>
          <div className="text-xs flex gap-2 items-center">
            <span> Page {page + 1}</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="border px-2 py-1 text-xs rounded"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-xs">rows per page</span>
          </div>
          <button
            onClick={handleNextPage}
            disabled={!ModalData || ModalData.length < pageSize}
            className="px-3 py-1 border text-xs rounded disabled:opacity-50 bg-[#2A5775] text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalMuiTable;
